import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { ArrowLeft, Clock, MapPin, MessagesSquare, Tag, Trash2 } from 'lucide-react';
import { db, storage } from '../../shared/lib/firebase';
import { useAuth } from '../auth/useAuth';
import { usePermissions } from '../../shared/hooks/usePermissions';
import { Skeleton } from '../../shared/ui/Skeleton';
import { Badge } from '../../shared/ui/Badge';
import { Button } from '../../shared/ui/Button';
import { Avatar } from '../../shared/ui/Avatar';
import { EmptyState } from '../../shared/ui/EmptyState';
import { timeAgo } from '../../shared/utils/timeAgo';
import { scrollToEnd } from '../../shared/utils/scrollToEnd';
import { useToast } from '../../shared/hooks/useToast';
import { useConfirmation } from '../../shared/hooks/useConfirmation';
import { useTicket } from './hooks/useTicket';
import { useTicketMessages } from './hooks/useTicketMessages';
import { TicketMessageBubble } from './components/TicketMessageBubble';
import { TicketComposer, type ComposerSubmit } from './components/TicketComposer';
import {
  STATUS_LABELS, PRIORITY_LABELS, STATUS_TONE, PRIORITY_TONE, isUnreadForMe, isRemoved,
} from './lib/support';
import type { SupportAttachment } from './lib/types';
import styles from './SupportTicketView.module.css';

export function SupportTicketView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userDoc } = useAuth();
  const { hasPermission } = usePermissions();
  const isManager = hasPermission('MANAGE_SUPPORT_TICKETS');
  const toast = useToast();
  const { confirm } = useConfirmation();
  const { ticket, loading, markRead } = useTicket(id);
  const { messages } = useTicketMessages(id);

  // Na clínica todo mundo (autor ou gerente) fala como 'owner'; 'admin' é só o Ardix, pela Central.
  useEffect(() => {
    if (ticket && isUnreadForMe(ticket, currentUser?.uid ?? '', isManager)) markRead();
  }, [ticket?.unreadForOwner, ticket?.createdBy, ticket?.openedByArdix, isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  // abre (e acompanha) a conversa na mensagem mais nova: sobe até a área que rola e vai ao fim
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const id = requestAnimationFrame(() => scrollToEnd(threadRef.current?.lastElementChild as HTMLElement | null)); // a conversa é quem rola
    return () => cancelAnimationFrame(id);
  }, [messages.length]);

  async function send({ body, files }: ComposerSubmit) {
    if (!id || !currentUser) return;
    const name = userDoc?.name ?? currentUser.displayName ?? currentUser.email ?? 'Usuário';
    const messageRef = doc(collection(db, 'support_tickets', id, 'messages'));
    const attachments: SupportAttachment[] = [];
    for (const f of files) {
      const path = `support_attachments/${id}/${messageRef.id}/${Date.now()}_${f.name}`;
      await uploadBytes(ref(storage, path), f, { contentType: f.type });
      attachments.push({ path, name: f.name, contentType: f.type, size: f.size });
    }
    const batch = writeBatch(db);
    batch.set(messageRef, {
      body, authorId: currentUser.uid, authorName: name, authorRole: 'owner',
      attachments, createdAt: serverTimestamp(),
    });
    await batch.commit();
  }

  // trava de clique duplo (P108 #4): o 2º update é negado pela regra (removedAt já existe) e mostraria um erro a mais
  const removingRef = useRef(false);
  const [removing, setRemoving] = useState(false);

  async function removeFromList() {
    if (!id || !currentUser || removingRef.current) return;
    removingRef.current = true;
    setRemoving(true);
    const ok = await confirm({
      title: 'Remover da lista?',
      description: 'O chamado some da lista da clínica. O Suporte Ardix continua vendo. Se alguém responder, ele volta.',
      confirmLabel: 'Remover da lista',
      danger: true,
    });
    if (!ok) {
      removingRef.current = false;
      setRemoving(false);
      return;
    }
    try {
      await updateDoc(doc(db, 'support_tickets', id), { removedAt: serverTimestamp(), removedBy: currentUser.uid });
      toast.success('Chamado removido da lista.');
      navigate('/support');
    } catch {
      removingRef.current = false;
      setRemoving(false);
      toast.error('Não foi possível remover o chamado da lista. Tente novamente.');
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton count={1} height={92} />
        <Skeleton count={4} height={64} />
      </div>
    );
  }
  if (!ticket) {
    return (
      <div className={styles.page}>
        <EmptyState
          icon={<MessagesSquare size={40} />}
          title="Chamado não encontrado"
          description="Ele pode ter sido removido ou você não tem acesso."
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button className={styles.back} onClick={() => navigate('/support')} aria-label="Voltar">
          <ArrowLeft size={18} />
        </button>
        <div className={styles.headerMain}>
          <div className={styles.headerTop}>
            <h1 className={styles.subject}>{ticket.subject}</h1>
            <Badge tone={STATUS_TONE[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
            <Badge tone={PRIORITY_TONE[ticket.priority]}>{PRIORITY_LABELS[ticket.priority]}</Badge>
            {!isRemoved(ticket) && (isManager || ticket.createdBy === currentUser?.uid) ? (
              <Button variant="ghost" size="sm" leftIcon={<Trash2 size={14} />} onClick={removeFromList} disabled={removing}>
                Remover da lista
              </Button>
            ) : null}
          </div>
          <div className={styles.meta}>
            <span className={styles.metaItem}>
              <Avatar name={ticket.createdByName} size="xs" /> {ticket.createdByName}
            </span>
            {ticket.category ? (
              <span className={styles.metaItem}><Tag size={13} aria-hidden /> {ticket.category}</span>
            ) : null}
            <span className={styles.metaItem}>
              <Clock size={13} aria-hidden /> aberto {timeAgo(ticket.createdAt?.toMillis())}
            </span>
            {ticket.originPath ? (
              <span className={styles.metaItem}><MapPin size={13} aria-hidden /> {ticket.originPath}</span>
            ) : null}
            {ticket.openedByArdix ? <Badge tone="primary">Aberto pelo Suporte Ardix</Badge> : null}
          </div>
        </div>
      </header>

      <div className={styles.thread} ref={threadRef}>
        {messages.length === 0 ? (
          <div className={styles.threadEmpty}>Nenhuma mensagem ainda.</div>
        ) : (
          messages.map((m) => (
            <TicketMessageBubble key={m.id} message={m} mine={m.authorId === currentUser?.uid} />
          ))
        )}
      </div>

      <TicketComposer onSend={send} disabled={ticket.status === 'closed'} />
    </div>
  );
}
